/**
 * Compliance Rule Book — admin only.
 *
 * The rule book is versioned, never overwritten. That distinction drives the
 * whole screen: changing a rule is two different actions with two different
 * consequences, and the interface has to make the difference obvious BEFORE the
 * click, not explain it afterwards.
 *
 *   Amend    the law changed   → closes this version, opens the next.
 *                                A checklist for an earlier period still
 *                                resolves the way it did at the time.
 *   Correct  we got it wrong   → edits in place. Right only when the old text
 *                                was never right, because it rewrites history.
 *
 * There is no delete. A rule that applied to a past period cannot be
 * un-applied — retiring closes the version instead.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiPlus, FiClock, FiAlertTriangle, FiUpload, FiDownload, FiCheckCircle, FiRotateCcw } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import Modal from '../components/common/Modal';
import EmptyState from '../components/common/EmptyState';
import { Select, DateField, inputClass, labelClass, textareaClass } from '../components/common/FormControls';
import { complianceRulesAPI } from '../services/api';
import { useUI } from '../context/UIContext';
import { confirmAction, promptForDate, esc } from '../utils/dialog';

const PERIODICITIES = ['annual', 'fy-linked', 'quarterly', 'monthly', 'event-based', 'one-time'];
const STATUS_FILTERS = [
  { value: 'current', label: 'In force' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'all', label: 'All versions' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const toIso = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const EMPTY_RULE = {
  ruleKey: '', act: '', section: '', obligation: '', authority: '', formCode: '',
  periodicity: 'annual', effectiveFrom: '',
  jurisdiction: { level: 'central', states: [] },
  appliesWhen: '', source: { citation: '', url: '' }, notes: '',
};

/** A quiet divider so a fifteen-field form reads as four short ones. */
function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2.5 pt-1">
      <span className="text-[10px] font-bold uppercase tracking-[.9px] text-[#9a9080] whitespace-nowrap">{children}</span>
      <span className="flex-1 h-px" style={{ background: '#e8e3d8' }} />
    </div>
  );
}

/** JSON in a textarea, checked server-side before it can be saved. */
function ExpressionField({ value, onChange, problems, onCheck, checking }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={labelClass} style={{ marginBottom: 0 }}>Applies when</div>
        <Button variant="ghost" size="sm" className="ml-auto !py-1 !px-2.5 text-[11px]" onClick={onCheck} disabled={checking}>
          {checking ? 'Checking…' : 'Check expression'}
        </Button>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={textareaClass}
        rows={7}
        spellCheck={false}
        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11.5px' }}
        placeholder={'{ "field": "establishments[].employeeCount", "op": ">=", "value": 20 }'}
      />
      <div className="mt-1 text-[10.5px] text-[#7a7060] leading-snug">
        A leaf is <code>field / op / value</code>. Combine with <code>all</code>, <code>any</code> or <code>not</code>.
        Per-premises fields take a <code>quantifier</code> — the default <code>any</code> is what EPF and ESI ask, since
        they test one establishment rather than the company total. Dated series take a <code>select</code>.
      </div>
      {problems?.length > 0 && (
        <div className="mt-2 rounded-lg border px-2.5 py-2" style={{ borderColor: '#e0c9a0', background: '#fdf8ee' }}>
          <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: '#8a6a1f' }}>
            <FiAlertTriangle size={12} /> {problems.length} problem{problems.length === 1 ? '' : 's'}
          </div>
          <ul className="mt-1 space-y-0.5">
            {problems.map((p, i) => (
              <li key={i} className="text-[11px] text-[#5a5348]">{p}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdminComplianceRulesPage() {
  const { showToast } = useUI();

  const [rules, setRules] = useState([]);
  const [acts, setActs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [filters, setFilters] = useState({ act: '', jurisdiction: '', periodicity: '', q: '', status: 'current' });

  // mode: 'create' | 'correct' | 'amend' | null
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE);
  const [exprText, setExprText] = useState('');
  const [problems, setProblems] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);

  // Bulk import. `report` holds the dry run until it is applied — nothing is
  // written until someone has read what would be created and what would not.
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [report, setReport] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await complianceRulesAPI.list(filters);
      setRules(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      // Never silently render an empty rule book: an admin seeing "no rules"
      // when the request failed would reasonably conclude the rule book is gone.
      setLoadError(e?.message || 'Could not load the rule book.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { complianceRulesAPI.acts().then((r) => setActs(r.data || [])).catch(() => {}); }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => {
    setForm(EMPTY_RULE); setExprText(''); setProblems([]); setEditor({ mode: 'create' });
  };

  const openEditor = (rule, mode) => {
    setForm({
      ...EMPTY_RULE,
      ...rule,
      effectiveFrom: toIso(rule.effectiveFrom),
      jurisdiction: { level: rule.jurisdiction?.level || 'central', states: rule.jurisdiction?.states || [] },
      source: { citation: rule.source?.citation || '', url: rule.source?.url || '' },
    });
    setExprText(rule.appliesWhen ? JSON.stringify(rule.appliesWhen, null, 2) : '');
    setProblems([]);
    setEditor({ mode, rule });
  };

  /** Parse the textarea. Returns undefined when blank — a rule may have no condition. */
  const parseExpression = () => {
    const t = exprText.trim();
    if (!t) return { ok: true, value: undefined };
    try { return { ok: true, value: JSON.parse(t) }; }
    catch (e) { return { ok: false, error: `Not valid JSON — ${e.message}` }; }
  };

  const checkExpression = async () => {
    const parsed = parseExpression();
    if (!parsed.ok) { setProblems([parsed.error]); return; }
    if (parsed.value === undefined) { setProblems([]); showToast('No condition — this rule applies to every company it is scoped to', 'info'); return; }
    setChecking(true);
    try {
      const res = await complianceRulesAPI.validateExpression(parsed.value);
      setProblems(res.data.problems || []);
      if (res.data.valid) showToast('Expression is valid', 'success');
    } catch (e) {
      setProblems([e?.message || 'Could not check the expression']);
    } finally {
      setChecking(false);
    }
  };

  const save = async () => {
    const parsed = parseExpression();
    if (!parsed.ok) { setProblems([parsed.error]); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        appliesWhen: parsed.value ?? null,
        jurisdiction: {
          level: form.jurisdiction.level,
          // A central rule must not name states, and a state rule must. The model
          // refuses either way round; clearing here keeps the form from sending
          // a combination it already knows is invalid.
          states: form.jurisdiction.level === 'state' ? form.jurisdiction.states : [],
        },
      };

      if (editor.mode === 'create') {
        await complianceRulesAPI.create(payload);
        showToast(`Created ${payload.ruleKey} at version 1`, 'success');
      } else if (editor.mode === 'correct') {
        await complianceRulesAPI.correct(editor.rule._id, payload);
        showToast(`Corrected ${payload.ruleKey} version ${editor.rule.version}`, 'success');
      } else {
        const { effectiveFrom, ...changes } = payload;
        if (!effectiveFrom) { setProblems(['An amendment needs the date the change in the law took effect']); setSaving(false); return; }
        const res = await complianceRulesAPI.amend(editor.rule.ruleKey, changes, effectiveFrom);
        showToast(`${payload.ruleKey} amended — version ${res.data.version} in force from ${fmtDate(effectiveFrom)}`, 'success');
      }
      setEditor(null);
      await load();
    } catch (e) {
      setProblems(e?.problems?.length ? e.problems : [e?.message || 'Save failed']);
    } finally {
      setSaving(false);
    }
  };

  const retire = async (rule) => {
    const confirmed = await confirmAction({
      title: `Retire ${rule.ruleKey}?`,
      message: `<p><strong>${esc(rule.obligation)}</strong></p>
        <p>This closes the standing version. <strong>It is not a delete</strong> — the rule still applied to
        every period before the date you give, and a checklist covering those periods keeps showing it.</p>`,
      confirmText: 'Choose the date',
      tone: 'danger',
    });
    if (!confirmed) return;

    const on = await promptForDate({
      title: 'Ceases on',
      message: 'The first day on which the obligation no longer applies.',
      label: 'Date',
      confirmText: 'Retire this rule',
      validate: (v) => (new Date(v) <= new Date(rule.effectiveFrom)
        ? `Must be after ${fmtDate(rule.effectiveFrom)}, when the rule took effect`
        : undefined),
    });
    if (!on) return;

    try {
      await complianceRulesAPI.retire(rule.ruleKey, on, '');
      showToast(`${rule.ruleKey} retired with effect from ${fmtDate(on)}`, 'success');
      await load();
    } catch (e) {
      showToast(e?.message || 'Could not retire the rule', 'error');
    }
  };

  /**
   * Adopt an older version's wording again.
   *
   * This does NOT reopen the old version — that would rewrite what past
   * checklists said. It opens a NEW version carrying the same content, so the
   * history reads "v1 applied, then v2, then v1's wording again".
   */
  const revertTo = async (version) => {
    const confirmed = await confirmAction({
      title: `Go back to version ${version.version}?`,
      message: `<p>The version in force closes, and a <strong>new version</strong> opens carrying
        version ${version.version}'s wording.</p>
        <p style="color:#7a7060">Version ${version.version} itself is not reopened — every period it already
        covered keeps resolving exactly as it does now. History does not move.</p>`,
      confirmText: 'Choose the date',
    });
    if (!confirmed) return;

    const from = await promptForDate({
      title: 'The old wording applies again from',
      label: 'Date',
      confirmText: `Open a new version from v${version.version}`,
    });
    if (!from) return;

    try {
      const res = await complianceRulesAPI.revertTo(history.ruleKey, version.version, from);
      showToast(`Version ${res.data.version} now carries v${version.version}'s wording, from ${fmtDate(from)}`, 'success');
      setHistory(null);
      await load();
    } catch (e) {
      showToast(e?.message || 'Could not revert', 'error');
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await complianceRulesAPI.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'compliance-rule-book-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e?.message || 'Could not download the template', 'error');
    }
  };

  /** Always a dry run first — `apply` only ever runs on a file already reported on. */
  const runImport = async (file, apply) => {
    setImporting(true);
    try {
      const res = await complianceRulesAPI.importWorkbook(file, apply);
      setReport(res.data);
      setPendingFile(apply ? null : file);
      if (apply) {
        showToast(`Imported ${res.data.created} rule${res.data.created === 1 ? '' : 's'}`, 'success');
        await load();
      }
    } catch (e) {
      showToast(e?.message || 'Import failed', 'error');
      setReport(null);
    } finally {
      setImporting(false);
    }
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) runImport(file, false);
  };

  const openHistory = async (rule) => {
    try {
      const res = await complianceRulesAPI.versions(rule.ruleKey);
      setHistory({ ruleKey: rule.ruleKey, versions: res.data || [] });
    } catch (e) {
      showToast(e?.message || 'Could not load version history', 'error');
    }
  };

  const actOptions = useMemo(() => [{ value: '', label: 'All Acts' }, ...acts.map((a) => ({ value: a, label: a }))], [acts]);

  return (
    <AppLayout>
      <div className="p-6">
        <PageHeader
          title="Compliance Rule Book"
          subtitle="One record per obligation, per version. Rules are never overwritten — history does not move."
        />

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9a9080]" />
          <input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className={inputClass}
            style={{ paddingLeft: 32 }}
            placeholder="Search obligation, Act, section or key…"
          />
        </div>
        <div className="w-[220px]">
          <Select value={filters.act} onChange={(v) => setFilters((f) => ({ ...f, act: v }))} options={actOptions} />
        </div>
        <div className="w-[150px]">
          <Select
            value={filters.jurisdiction}
            onChange={(v) => setFilters((f) => ({ ...f, jurisdiction: v }))}
            options={[{ value: '', label: 'Any jurisdiction' }, { value: 'central', label: 'Central' }, { value: 'state', label: 'State' }]}
          />
        </div>
        <div className="w-[150px]">
          <Select
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={STATUS_FILTERS}
          />
        </div>
        <Button variant="ghost" onClick={downloadTemplate} leftIcon={<FiDownload size={14} />}>Template</Button>
        <Button variant="ghost" onClick={() => fileRef.current?.click()} disabled={importing} leftIcon={<FiUpload size={14} />}>
          {importing ? 'Reading…' : 'Import'}
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFilePicked} className="hidden" />
        <Button variant="primary" onClick={openCreate} leftIcon={<FiPlus size={14} />}>New rule</Button>
      </div>

      {/* ── Import report ─────────────────────────────────────────────────
          A dry run by default. Rules decide what a client is told they must
          file, so what would be created is shown before anything is. */}
      {report && (
        <div className="rounded-xl border p-3 mb-4" style={{ borderColor: '#ddd6c6', background: '#faf9f6' }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[13px] font-bold text-[#3a352c]">
              {report.dryRun ? 'Dry run — nothing has been written' : `Imported ${report.created} rule${report.created === 1 ? '' : 's'}`}
            </div>
            <Chip bg="#e4f5f3" color="#145f5f">{report.accepted} ready</Chip>
            {report.rejected.length > 0 && <Chip bg="#f3ebd7" color="#8a6a1f">{report.rejected.length} rejected</Chip>}
            <span className="text-[11px] text-[#7a7060]">{report.rowsRead} rows read from “{report.sheet}”</span>
            <button type="button" onClick={() => { setReport(null); setPendingFile(null); }}
              className="ml-auto text-[11px] font-semibold text-[#7a7060] hover:text-[#8a6a1f]">Dismiss</button>
          </div>

          {report.rejected.length > 0 && (
            <div className="rounded-lg border overflow-hidden mb-2" style={{ borderColor: '#e0c9a0' }}>
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: '#fdf8ee' }}>
                    {['Row', 'Rule key', 'Why it was rejected'].map((h) => (
                      <th key={h} className="text-left px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[.7px]" style={{ color: '#8a6a1f' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.rejected.map((r) => (
                    <tr key={r.row} style={{ borderTop: '1px solid #f0e4cc' }}>
                      <td className="px-2.5 py-1.5 text-[11.5px] text-[#5a5348] align-top">{r.row}</td>
                      <td className="px-2.5 py-1.5 text-[11px] text-[#5a5348] align-top" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.ruleKey || '—'}</td>
                      <td className="px-2.5 py-1.5 text-[11.5px] text-[#5a5348]">
                        {r.problems.map((p, i) => <div key={i}>{p}</div>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.dryRun && report.accepted > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm" disabled={importing}
                onClick={() => pendingFile && runImport(pendingFile, true)}
                leftIcon={<FiCheckCircle size={13} />}>
                Import the {report.accepted} accepted row{report.accepted === 1 ? '' : 's'}
              </Button>
              {report.rejected.length > 0 && (
                <span className="text-[11px] text-[#7a7060]">
                  The rejected rows are skipped — fix them in the sheet and upload again.
                </span>
              )}
            </div>
          )}

          {report.dryRun && report.accepted === 0 && (
            <div className="text-[11.5px] text-[#7a7060]">No row can be imported as it stands.</div>
          )}
        </div>
      )}

      {loadError ? (
        <div className="rounded-xl border px-4 py-3 mb-4" style={{ borderColor: '#e0c9a0', background: '#fdf8ee' }}>
          <div className="flex items-center gap-2 text-[13px] font-bold" style={{ color: '#8a6a1f' }}>
            <FiAlertTriangle size={14} /> {loadError}
          </div>
          <div className="mt-1 text-[12px] text-[#5a5348]">
            The rule book was not loaded — this is not an empty rule book.
          </div>
          <Button variant="ghost" size="sm" className="mt-2" onClick={load}>Try again</Button>
        </div>
      ) : loading ? (
        <div className="py-12 text-center text-[13px] text-[#7a7060]">Loading the rule book…</div>
      ) : !rules.length ? (
        <EmptyState
          title="No rules match"
          description={filters.q || filters.act ? 'Nothing matches these filters.' : 'The rule book is empty. Add the first rule, or load many at once from a spreadsheet.'}
        />
      ) : (
        <>
          <div className="mb-2 text-[12px] text-[#7a7060]">{total} rule{total === 1 ? '' : 's'}</div>
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#ddd6c6' }}>
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                  {['Obligation', 'Act / section', 'Where', 'Periodicity', 'In force', 'Ver.', ''].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-[.7px] text-[#7a7060]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.map((r, i) => (
                  <tr key={r._id} style={{ borderBottom: i < rules.length - 1 ? '1px solid #e8e3d8' : 'none', background: i % 2 === 0 ? '#fff' : '#faf9f6' }}>
                    <td className="px-3 py-2">
                      <div className="text-[12.5px] font-semibold text-[#3a352c]">{r.obligation}</div>
                      <div className="text-[10.5px] text-[#9a9080]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.ruleKey}</div>
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[#5a5348]">
                      {r.act}{r.section ? <span className="text-[#9a9080]"> · {r.section}</span> : null}
                      {r.engineRef?.formKey && <div className="text-[10px] text-[#145f5f]">due date from the engine ({r.engineRef.formKey})</div>}
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[#5a5348]">
                      {r.jurisdiction?.level === 'state'
                        ? (r.jurisdiction.states || []).join(', ') || '—'
                        : 'Central'}
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[#5a5348]">{r.periodicity}</td>
                    <td className="px-3 py-2 text-[11.5px] text-[#5a5348]">
                      {fmtDate(r.effectiveFrom)}
                      {r.effectiveTo
                        ? <span className="text-[#9a9080]"> → {fmtDate(r.effectiveTo)}</span>
                        : <Chip bg="#e4f5f3" color="#145f5f">current</Chip>}
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[#5a5348]">v{r.version}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button type="button" onClick={() => openHistory(r)} title="Version history"
                          className="p-1.5 rounded-md text-[#7a7060] hover:bg-[#f3ebd7] hover:text-[#8a6a1f]">
                          <FiClock size={13} />
                        </button>
                        {!r.effectiveTo && (
                          <>
                            <button type="button" onClick={() => openEditor(r, 'amend')}
                              className="px-2 py-1 rounded-md text-[11px] font-semibold text-[#145f5f] hover:bg-[#e4f5f3]"
                              title="The law changed — closes this version and opens the next">
                              Amend
                            </button>
                            <button type="button" onClick={() => openEditor(r, 'correct')}
                              className="px-2 py-1 rounded-md text-[11px] font-semibold text-[#7a7060] hover:bg-[#f3ebd7]"
                              title="We got it wrong — edits this version in place">
                              Correct
                            </button>
                            <button type="button" onClick={() => retire(r)}
                              className="px-2 py-1 rounded-md text-[11px] font-semibold text-[#7a7060] hover:bg-[#f3ebd7]"
                              title="Repealed — closes this version. Never a delete.">
                              Retire
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Editor ────────────────────────────────────────────────────────── */}
      <Modal
        open={Boolean(editor)}
        onClose={() => setEditor(null)}
        size="xl"
        // The body owns its padding so the section dividers can run edge to edge.
        bodyClassName="!p-0"
        title={editor?.mode === 'create' ? 'New rule'
          : editor?.mode === 'amend' ? `Amend ${editor.rule.ruleKey} — the law changed`
            : `Correct ${editor?.rule?.ruleKey} v${editor?.rule?.version} — we got it wrong`}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setEditor(null)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : editor?.mode === 'amend' ? 'Open new version' : 'Save'}
            </Button>
          </>
        )}
      >
        {editor && (
          <div className="px-5 py-4 space-y-4">
            {/* The consequence, stated before the fields — this is the only thing
                that keeps Amend and Correct from being clicked interchangeably. */}
            <div className="rounded-xl px-3.5 py-2.5 text-[12px] leading-relaxed"
              style={{
                background: editor.mode === 'amend' ? '#e4f5f3' : editor.mode === 'correct' ? '#fdf8ee' : '#faf9f6',
                color: '#3a352c',
                borderLeft: `3px solid ${editor.mode === 'amend' ? '#145f5f' : editor.mode === 'correct' ? '#8a6a1f' : '#ddd6c6'}`,
              }}>
              {editor.mode === 'amend'
                ? 'This closes the standing version on the date you give and opens the next. A checklist for an earlier period still resolves against the old rule — history does not move.'
                : editor.mode === 'correct'
                  ? 'This edits the version in place, which rewrites what a past checklist would say. Right only when the old text was never right. If the law changed, use Amend instead.'
                  : 'A new rule starts at version 1 and stays in force until it is amended or retired.'}
            </div>

            <SectionLabel>What the obligation is</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className={labelClass}>Rule key</div>
                <input value={form.ruleKey} onChange={(e) => setField('ruleKey', e.target.value)}
                  className={inputClass} placeholder="epf.monthly-contribution"
                  disabled={editor.mode !== 'create'}
                  style={{ fontFamily: "'JetBrains Mono', monospace" }} />
                <div className="mt-1 text-[10px] text-[#7a7060]">
                  {editor.mode === 'create' ? 'Lowercase, dot or dash separated. Stable across every version.' : 'Fixed — it is what makes each version the same obligation.'}
                </div>
              </div>
              <div>
                <div className={labelClass}>Obligation</div>
                <input value={form.obligation} onChange={(e) => setField('obligation', e.target.value)}
                  className={inputClass} placeholder="Monthly contribution and return" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <div className={labelClass}>Act</div>
                <input value={form.act} onChange={(e) => setField('act', e.target.value)}
                  className={inputClass} placeholder="Employees' Provident Funds Act 1952" />
              </div>
              <div>
                <div className={labelClass}>Section</div>
                <input value={form.section} onChange={(e) => setField('section', e.target.value)}
                  className={inputClass} placeholder="s.1(3)" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className={labelClass}>Authority</div>
                <input value={form.authority} onChange={(e) => setField('authority', e.target.value)} className={inputClass} placeholder="EPFO" />
              </div>
              <div>
                <div className={labelClass}>Form</div>
                <input value={form.formCode} onChange={(e) => setField('formCode', e.target.value)} className={inputClass} placeholder="ECR" />
              </div>
              <div>
                <div className={labelClass}>Periodicity</div>
                <Select value={form.periodicity} onChange={(v) => setField('periodicity', v)} options={PERIODICITIES} />
              </div>
            </div>

            <SectionLabel>Where it bites</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className={labelClass}>Jurisdiction</div>
                <Select
                  value={form.jurisdiction.level}
                  onChange={(v) => setField('jurisdiction', { ...form.jurisdiction, level: v })}
                  options={[{ value: 'central', label: 'Central' }, { value: 'state', label: 'State' }]}
                />
              </div>
              <div className="md:col-span-2">
                <div className={labelClass}>States</div>
                <input
                  value={(form.jurisdiction.states || []).join(', ')}
                  onChange={(e) => setField('jurisdiction', {
                    ...form.jurisdiction,
                    states: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                  className={inputClass}
                  disabled={form.jurisdiction.level !== 'state'}
                  placeholder="Gujarat, Madhya Pradesh"
                />
                <div className="mt-1 text-[10px] text-[#7a7060]">
                  {form.jurisdiction.level === 'state'
                    ? 'Required — a state rule that names none would attach to every company in the country.'
                    : 'Central rules apply everywhere and must not name states.'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className={labelClass}>{editor.mode === 'amend' ? 'The change took effect on' : 'In force from'}</div>
                <DateField value={toIso(form.effectiveFrom)} onChange={(v) => setField('effectiveFrom', v)}
                  placeholder="Effective from" />
                {editor.mode === 'amend' && (
                  <div className="mt-1 text-[10px] text-[#7a7060]">
                    The standing version is closed on this date. It must be after {fmtDate(editor.rule.effectiveFrom)}.
                  </div>
                )}
              </div>
              <div>
                <div className={labelClass}>Source citation</div>
                <input value={form.source.citation} onChange={(e) => setField('source', { ...form.source, citation: e.target.value })}
                  className={inputClass} placeholder="Corporate Compliance Framework, section 3" />
              </div>
            </div>

            <SectionLabel>When it applies</SectionLabel>
            <ExpressionField
              value={exprText}
              onChange={setExprText}
              problems={problems}
              onCheck={checkExpression}
              checking={checking}
            />

            <div>
              <div className={labelClass}>Notes</div>
              <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} className={textareaClass} rows={2} />
            </div>
          </div>
        )}
      </Modal>

        {/* ── Version history ─────────────────────────────────────────────── */}
        <Modal
          open={Boolean(history)}
          onClose={() => setHistory(null)}
          size="lg"
          title={`Version history — ${history?.ruleKey || ''}`}
        >
          <div className="space-y-2.5">
            <div className="rounded-lg px-3 py-2 text-[11.5px] leading-snug" style={{ background: '#faf9f6', color: '#5a5348' }}>
              Every version is kept. A checklist for a past period is computed against the rule that was in
              force then. Going back to an older wording opens a <strong>new</strong> version carrying it —
              the old version is never reopened, so nothing already computed changes.
            </div>

            {(history?.versions || []).map((v) => {
              const isCurrent = !v.effectiveTo;
              return (
                <div key={v._id} className="rounded-xl border overflow-hidden"
                  style={{ borderColor: isCurrent ? '#145f5f' : '#ddd6c6', background: isCurrent ? '#fff' : '#faf9f6' }}>
                  <div className="flex flex-wrap items-center gap-2 px-3 py-2"
                    style={{ background: isCurrent ? '#e4f5f3' : '#f5f2ea' }}>
                    <span className="text-[13px] font-bold" style={{ color: isCurrent ? '#145f5f' : '#5a5348' }}>
                      Version {v.version}
                    </span>
                    {isCurrent
                      ? <Chip bg="#145f5f" color="#fff">in force</Chip>
                      : <Chip bg="#e8e3d8" color="#7a7060">superseded</Chip>}
                    <span className="text-[11.5px] text-[#7a7060]">
                      {fmtDate(v.effectiveFrom)} → {v.effectiveTo ? fmtDate(v.effectiveTo) : 'present'}
                    </span>
                    {!isCurrent && (
                      <Button variant="ghost" size="sm"
                        className="ml-auto !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#145f5f]/[0.08] hover:!text-[#145f5f]"
                        onClick={() => revertTo(v)}
                        leftIcon={<FiRotateCcw size={12} />}>
                        Use this wording again
                      </Button>
                    )}
                  </div>

                  <div className="px-3 py-2.5 space-y-2">
                    <div className="text-[12.5px] font-semibold text-[#3a352c]">{v.obligation}</div>
                    {v.notes && <div className="text-[11.5px] text-[#7a7060] leading-snug">{v.notes}</div>}
                    {v.appliesWhen && (
                      <div>
                        <div className="text-[9.5px] font-bold uppercase tracking-[.7px] text-[#7a7060] mb-1">Applies when</div>
                        {/* Pretty-printed and wrapped: a single-line expression in a
                            horizontally scrolling box is unreadable, and this is the
                            part a reviewer most needs to actually read. */}
                        <pre className="text-[10.5px] text-[#5a5348] rounded-lg px-2.5 py-2 max-h-[190px] overflow-auto"
                          style={{ fontFamily: "'JetBrains Mono', monospace", background: '#f8f5ef', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {JSON.stringify(v.appliesWhen, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      </div>
    </AppLayout>
  );
}
